import config from '../config';

import { CSSProperties, useRef, useEffect, useCallback } from 'react';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
import QuizTypes from 'types/QuizTypes';

type Props = {
  style?: CSSProperties;
  id: string;
  quizNameSlug: string;
  quizHash: string | null;
  /**
   * When provided, QuizFrame switches to selective-reload mode:
   * the iframe is reloaded only when pageHash changes (i.e. only the data
   * relevant to this specific screen changed), rather than on every quizHash
   * change. Pass a stable JSON fingerprint of the quiz fields this screen
   * depends on (global settings or a single dynamic page object).
   */
  pageHash?: string;
  preview: 'quiz' | 'liveLeaderboard';
  page?: string;
  pageRef?: string;
};

const QuizFrame:React.FC<Props> = ({
  style = {}, id, quizNameSlug, quizHash, pageHash, preview, page, pageRef
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const prevPageHashRef = useRef<string | null>(null);
  const quiz = useAppSelector(selectQuiz);

  const isPollQuizzes = quiz && [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quiz.type);
  const isTeamSelectorQuiz = quiz && quiz.type === QuizTypes.TeamSelector;

  const quizSrc = `${config.quizURL}/quiz/${quizNameSlug}?preview=true&hasHash=true`;
  const liveLeaderboardSrc = `${config.quizURL}/quiz/${quizNameSlug}/live-${isPollQuizzes ? 'results' : isTeamSelectorQuiz ? 'teamboard' : 'leaderboard'}?hasHash=true`;

  const src = preview === 'quiz' ? quizSrc : liveLeaderboardSrc;

  const sendPreview = useCallback(() => {
    if (!iframeRef.current || !quizHash) return;

    const message: any = {
      action: 'preview',
      quizHash
    };

    if (page) message.page = page;
    if (pageRef) message.pageRef = pageRef;

    iframeRef.current.contentWindow?.postMessage(message, '*');
  }, [quizHash, page, pageRef]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'READY' && event.source === iframeRef.current?.contentWindow) {
        sendPreview();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [sendPreview]);

  // SELECTIVE MODE: reload only this iframe when its relevant data fingerprint
  // changes. Reloading triggers the READY→sendPreview flow with the latest
  // quizHash, which is the mechanism the quiz app relies on.
  useEffect(() => {
    if (pageHash === undefined) return;
    if (pageHash === prevPageHashRef.current) return;
    prevPageHashRef.current = pageHash;
    if (iframeRef.current && quizNameSlug) {
      iframeRef.current.src = src;
    }
  // src intentionally included so a slug/url change also triggers reload
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageHash]);

  // LEGACY MODE: reload on every quizHash change (original behaviour).
  // Only active when caller does not pass pageHash.
  useEffect(() => {
    if (pageHash !== undefined) return;
    if (!iframeRef.current || !quizNameSlug) return;
    iframeRef.current.src = src;
  }, [pageHash, quizHash, src, quizNameSlug]);

  if (!quizHash || !quizNameSlug) return null;
  return (
    <iframe
      ref={iframeRef}
      style={style}
      id={id}
      title={`${preview}Preview`}
    />
  );
};

export default QuizFrame;
